import Edit from '@mui/icons-material/Edit';
import {
	Divider,
	Grid,
	MenuItem,
	Select,
	SelectChangeEvent,
	Tooltip,
	Typography
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { ITheme } from 'types/theme';
import { MessageConnectorContext } from 'hooks/useConnectorsContext';
import React, { useContext, useState } from 'react';
import {
	ConnectorIndexEnum,
	ConnectorTypeEnum,
	CustomAuthConnector,
	DEFAULT_CONNECTOR_ID,
	emailTypes
} from 'types/connectors';
import { getConnectorIcon } from './ConnectorEditDialogUtils';

interface IConnectorSelectProps {
	id: string;
	selectedValue: string;
	connectors: CustomAuthConnector[];
	connectorType: string;
	labelId: string;
	formik: any;
	disabled?: boolean;
	onSelect: (connector: CustomAuthConnector) => Promise<void>;
}
const ConnectorSelect = ({
	id: compId,
	selectedValue,
	connectors,
	connectorType,
	labelId,
	onSelect,
	formik,
	disabled
}: IConnectorSelectProps) => {
	const theme = useTheme<ITheme>();

	const { createConnector, editConnector } = useContext(
		MessageConnectorContext
	);

	const getDisplayName = (connectorId: string) => {
		const connector = connectors.filter((c) => c.common.id === connectorId)[0];
		return connector ? connector.common.displayName : DEFAULT_CONNECTOR_ID;
	};

	const onItemEdit = (
		e: React.BaseSyntheticEvent,
		connector: CustomAuthConnector
	) => {
		e.preventDefault();
		e.stopPropagation();
		editConnector(connector, formik);
	};

	const addConnectorButton = (type: ConnectorIndexEnum) => (
		<MenuItem
			onClick={() => {
				createConnector(type, formik);
			}}
			key={`create-button-${type}`}
			data-testid={`connector-create-${type}`}
		>
			<Box display="flex" flexDirection="row" flexGrow={1}>
				<Grid
					height={28}
					container
					direction="row"
					justifyContent="flex-start"
					alignItems="center"
				>
					<Grid item mr={1}>
						<img
							src={getConnectorIcon(type, theme)}
							alt={intl.tr(`connector.icon.alt.${type}`)}
						/>
					</Grid>
					<Grid item>
						<Typography fontSize="medium">
							{intl.tr(`connectors.create.new.${type}`)}
						</Typography>
					</Grid>
				</Grid>
			</Box>
		</MenuItem>
	);

	const getTargetConnector = (e: SelectChangeEvent<string>) =>
		connectors.filter(
			({ common }: CustomAuthConnector) => common.id === e.target.value
		)[0];

	const generateCreateServiceButtons = () => {
		switch (connectorType) {
			case ConnectorTypeEnum.EMAIL_PROVIDERS:
				return emailTypes.map((btn) => addConnectorButton(btn));
			case ConnectorTypeEnum.SMS_PROVIDERS:
				return addConnectorButton(ConnectorIndexEnum.TWILIO);
			default:
				return [];
		}
	};

	const [open, setOpen] = useState(false);

	return (
		<Select
			value={selectedValue || DEFAULT_CONNECTOR_ID}
			open={open}
			labelId={labelId}
			id={compId}
			data-testid={compId}
			renderValue={getDisplayName}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			onChange={(e) => onSelect(getTargetConnector(e))}
			label={intl.tr(`connectors.select.label`)}
			fullWidth
			disabled={disabled}
		>
			{connectors.map((connector) => {
				const { common } = connector;
				const { id, displayName } = common;
				return (
					<MenuItem
						value={id}
						key={`connector-${id}`}
						id={`custom-connector-${id}`}
						data-testid={`connector-select-item-${id}`}
					>
						<Box display="flex" flexDirection="row" flexGrow={1}>
							<Grid
								height={32}
								container
								direction="row"
								justifyContent="flex-start"
								alignItems="center"
							>
								<Grid item mr={1}>
									{id !== DEFAULT_CONNECTOR_ID && (
										<img
											src={getConnectorIcon(
												connector.providerProtocol as ConnectorIndexEnum,
												theme
											)}
											alt={intl.tr(
												`connector.icon.alt.${connector.providerProtocol}`
											)}
										/>
									)}
									{id === DEFAULT_CONNECTOR_ID && (
										<img
											src={getConnectorIcon('descope', theme)}
											width="20"
											height="20"
											alt={intl.tr('connector.icon.alt.descope')}
										/>
									)}
								</Grid>

								<Grid item>
									<Typography fontSize="medium">{displayName}</Typography>
								</Grid>
							</Grid>
							{id !== DEFAULT_CONNECTOR_ID && (
								<Grid
									container
									height={31}
									direction="row"
									justifyContent="flex-end"
									alignItems="center"
									style={{
										minWidth: '20px',
										overflow: 'hidden',
										backgroundPosition: 'center'
									}}
								>
									<Tooltip
										title={intl.tr('connectors.select.item.edit.tooltip')}
										placement="left"
									>
										<Edit
											fontSize="inherit"
											color="action"
											data-testid={`edit-connector-${id}`}
											onClick={(e) => onItemEdit(e, connector)}
										/>
									</Tooltip>
								</Grid>
							)}
						</Box>
					</MenuItem>
				);
			})}
			<Divider />
			{generateCreateServiceButtons()}
		</Select>
	);
};

ConnectorSelect.defaultProps = {
	disabled: false
};

export default ConnectorSelect;
